// W44 (2026-10-21): Pattern A mint-token exchange.
//
// The HubSpot card calls the api-server through hubspot.fetch
// (POST /v1/session/mint, v3-signature verified) and opens this iframe
// with ONLY `?s=<mint token>` on the URL. On mount the handshake reads
// that token, strips it from the address bar, and calls
// POST /v1/session/exchange exactly once. The reply is a 60-minute
// workspace bearer (`ws1.<token>`) plus the server-authoritative
// identity: userId, email, contactId, portalId, role.
//
// The exchange request carries NO Authorization header and NO X-User-*
// headers; the mint token in the JSON body is the whole credential.
// Every later request sends `Authorization: Bearer ws1.<token>` through
// the existing transports (api/transport.ts, screens/_shared
// buildSessionHeaders), which attach X-User-Id / X-User-Email only when
// `session.urlBootstrapped` is set, and an exchanged session never is.
//
// Transport decision (bearer in memory, not a cookie): the iframe is
// cross-site inside HubSpot's openIframeModal, where a SameSite=None
// cookie is subject to third-party-cookie blocking (INFERRED, not
// measured in the real modal). A page reload therefore loses the bearer
// and the already-used mint token answers 401 MINT_TOKEN_USED: the rep
// re-opens the workspace from the HubSpot record (one click).

import { defaultFetch } from './default-fetch.js';
import { isWorkspaceRole, type WorkspaceRole } from '../auth/workspace-role.js';

export const SESSION_QUERY_PARAM = 's';
export const SESSION_EXCHANGE_PATH = '/v1/session/exchange';
export const SESSION_TOKEN_PREFIX = 'ws1.';
export const DEFAULT_EXCHANGE_TIMEOUT_MS = 15_000;

// 32 random bytes, base64url, no padding -> 43 characters (api-server
// routes/workspace-session.mjs OPAQUE_TOKEN_PATTERN).
const MINT_TOKEN_RX = /^[A-Za-z0-9_-]{43}$/;

export interface ExchangedSession {
  sessionToken: string;
  // Unix ms when the workspace bearer stops being valid.
  expiresAt: number;
  portalId: number;
  userId: string;
  email: string;
  contactId: string;
  role: WorkspaceRole | null;
}

export type ExchangeResult =
  | { ok: true; session: ExchangedSession }
  | { ok: false; status: number; errorCode: string };

export interface ExchangeOptions {
  token: string;
  // Defaults to the same-origin relative path (nginx proxies /v1/ to the
  // api-server); the injected runtime base URL wins when present.
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

export function readMintTokenFromSearch(search: string | null | undefined): string | null {
  if (typeof search !== 'string' || search.length === 0) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return null;
  }
  const raw = params.get(SESSION_QUERY_PARAM);
  if (!raw) return null;
  const token = raw.trim();
  return MINT_TOKEN_RX.test(token) ? token : null;
}

/**
 * Remove `s` from the address bar (history.replaceState) so the mint
 * token never sits in history, referrers or screenshots. Every other
 * query parameter (for example ?preview=) is preserved untouched.
 */
export function stripMintTokenFromLocation(windowRef: Window | null | undefined): boolean {
  if (!windowRef) return false;
  try {
    const loc = windowRef.location;
    const params = new URLSearchParams(loc.search.startsWith('?') ? loc.search.slice(1) : loc.search);
    if (!params.has(SESSION_QUERY_PARAM)) return false;
    params.delete(SESSION_QUERY_PARAM);
    const rest = params.toString();
    const next = `${loc.pathname}${rest ? `?${rest}` : ''}${loc.hash ?? ''}`;
    windowRef.history.replaceState(windowRef.history.state, '', next);
    return true;
  } catch {
    return false;
  }
}

function readRuntimeBaseUrl(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as { __RUNTIME_API_BASE_URL__?: string }).__RUNTIME_API_BASE_URL__
      : undefined;
  if (typeof injected === 'string' && injected.length > 0) return injected;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const fromEnv = env?.VITE_RUNTIME_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return '';
}

function pickString(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function pickNumber(obj: Record<string, unknown>, key: string): number | null {
  const v = obj[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Parse the api-server's exchange reply. Null when any required field is off. */
export function parseExchangeResponse(body: unknown): ExchangedSession | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as Record<string, unknown>;
  const sessionToken = pickString(b, 'sessionToken');
  const expiresAtRaw = pickString(b, 'expiresAt');
  const portalId = pickNumber(b, 'portalId');
  const userId = pickString(b, 'userId');
  const contactId = pickString(b, 'contactId');
  if (!sessionToken || !sessionToken.startsWith(SESSION_TOKEN_PREFIX)) return null;
  if (!expiresAtRaw || portalId === null || !userId || !contactId) return null;
  const expiresAt = Date.parse(expiresAtRaw);
  if (!Number.isFinite(expiresAt)) return null;
  const email = typeof b['email'] === 'string' ? (b['email'] as string) : '';
  const roleRaw = b['role'];
  return {
    sessionToken,
    expiresAt,
    portalId,
    userId,
    email,
    contactId,
    role: isWorkspaceRole(roleRaw) ? roleRaw : null,
  };
}

export async function exchangeMintToken(options: ExchangeOptions): Promise<ExchangeResult> {
  const { token, fetchImpl = defaultFetch, timeoutMs = DEFAULT_EXCHANGE_TIMEOUT_MS } = options;
  const base = (options.baseUrl ?? readRuntimeBaseUrl()).replace(/\/+$/, '');
  const url = `${base}${SESSION_EXCHANGE_PATH}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ [SESSION_QUERY_PARAM]: token }),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timer);
    return { ok: false, status: 0, errorCode: controller.signal.aborted ? 'EXCHANGE_TIMEOUT' : 'NETWORK_ERROR' };
  }
  clearTimeout(timer);
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const code =
      body && typeof body === 'object' && typeof (body as { errorCode?: unknown }).errorCode === 'string'
        ? (body as { errorCode: string }).errorCode
        : `HTTP_${response.status}`;
    return { ok: false, status: response.status, errorCode: code };
  }
  const session = parseExchangeResponse(body);
  if (!session) return { ok: false, status: response.status, errorCode: 'EXCHANGE_RESPONSE_INVALID' };
  return { ok: true, session };
}

/** Rep-facing copy for a failed exchange. Never echoes the token. */
export function exchangeFailureMessage(result: { errorCode: string; status: number }): string {
  const reopen = 'Close this window and open the Sales Workspace from the HubSpot contact record again.';
  switch (result.errorCode) {
    case 'MINT_TOKEN_USED':
    case 'MINT_TOKEN_EXPIRED':
    case 'MINT_TOKEN_NOT_FOUND':
    case 'MINT_TOKEN_INVALID':
      return `This workspace link has already been used or has expired. ${reopen}`;
    case 'NETWORK_ERROR':
    case 'EXCHANGE_TIMEOUT':
      return `The workspace could not reach the session service. ${reopen}`;
    default:
      return `The workspace could not start a session (${result.errorCode}). ${reopen}`;
  }
}
